import React, { useState } from 'react';
import { 
  FileText, Search, Calendar, ShoppingBag, ChevronDown, Trash2, X, 
  Wallet, QrCode, ChevronUp, Truck, Sparkles, CheckCircle2, Clock, 
  Layers, Printer, Eye, ChevronRight 
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useClientPOs } from '@/hooks/useClientPOs';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import { useClientSession } from '@/lib/ClientSessionContext';
import BatchActionBar from '@/components/ui/BatchActionBar';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CustomDatePicker from '@/components/shared/CustomDatePicker';
import SuratJalanPrint from '@/components/production/SuratJalanPrint';

const defaultPOs = [
  {
    id: 'PO-2026-0501',
    varianLabel: 'Sun Zest Glowing Serum (1000 pcs)',
    date: '15 Mei 2026',
    statusProduksi: 'PROSES PRODUKSI',
    statusBayar: 'LUNAS',
    customerName: 'PT Cielmora Beauty / Asa',
    customerAddress: 'Jalan aster kavling Paspampres, Kota Batu, Kec. Ciomas, Kabupaten Bogor, Jawa Barat 16610',
    phone: '08567215745',
    courier: 'JNE REG',
    resi: '0123082600169080',
    shippingCost: 10000,
    dpPercentage: 100,
    items: [
      {
        nama: 'Sun Zest Glowing Serum',
        qty: 1000,
        hargaTotal: 16600000,
        hasImage: false
      }
    ],
    timeline: [
      { status: 'Pesanan Dibuat', date: '15 Mei 2026', desc: 'Pesanan Dibuat', done: true },
      { status: 'Dikonfirmasi Tim Satala', date: '15 Mei 2026', desc: 'Pembayaran Lunas', done: true },
      { status: 'Antrean Produksi', date: '15 Mei 2026', desc: 'Semua Item Ready', done: true },
      { status: 'Proses Produksi', date: '16 Mei 2026', desc: 'Sedang Diproduksi', done: true, active: true },
      { status: 'Pesanan Dikirim', date: '-', desc: '-', done: false },
      { status: 'Pesanan Selesai', date: '-', desc: '-', done: false }
    ],
    resiTimeline: []
  },
  {
    id: 'PO-2026-0505',
    varianLabel: 'Blue Zest Hydrating Toner (500 pcs)',
    date: '18 Mei 2026',
    statusProduksi: 'SEDANG PO ITEM',
    statusBayar: 'DP 50%',
    customerName: 'PT Cielmora Beauty / Asa',
    customerAddress: 'Jalan aster kavling Paspampres, Kota Batu, Kec. Ciomas, Kabupaten Bogor, Jawa Barat 16610',
    phone: '08567215745',
    courier: 'Lalamove Same Day',
    resi: 'LALA-9283712',
    shippingCost: 77500,
    dpPercentage: 50,
    items: [
      {
        nama: 'Blue Zest Hydrating Toner',
        qty: 500,
        hargaTotal: 12500000,
        hasImage: false
      }
    ],
    timeline: [
      { status: 'Pesanan Dibuat', date: '18 Mei 2026', desc: 'Pesanan Dibuat', done: true },
      { status: 'Dikonfirmasi Tim Satala', date: '18 Mei 2026', desc: 'DP 50% Diterima', done: true },
      { status: 'Sedang PO Item', date: '19 Mei 2026', desc: 'Pengadaan Bahan & Kemasan', done: true, active: true },
      { status: 'Antrean Produksi', date: '-', desc: 'Menunggu Semua Item Ready', done: false },
      { status: 'Proses Produksi', date: '-', desc: '-', done: false },
      { status: 'Pesanan Selesai', date: '-', desc: '-', done: false }
    ],
    resiTimeline: []
  }
];

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

export default function ClientPOHistory() {
  const { clientUser } = useClientSession();
  const { clientPOs, isLoading: loadingPOs, updatePO, deletePO } = useClientPOs();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const searchQuery = searchParams.get('q') || '';
  const statusFilter = searchParams.get('status') || 'ALL';
  const dateFilter = searchParams.get('date') || '';
  const sortOrder = searchParams.get('sort') || 'latest';

  const updateParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  React.useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter, dateFilter, sortOrder]);

  // Payment Continuation State
  const [payModalPO, setPayModalPO] = useState(null);
  const [payDpPct, setPayDpPct] = useState(50);
  const [payMethod, setPayMethod] = useState('bca');
  const [suratJalanOrder, setSuratJalanOrder] = useState(null);
  const [taskDetailModalPO, setTaskDetailModalPO] = useState(null);

  // Queries untuk status produksi real-time & surat jalan
  const { data: prodOrders = [] } = useQuery({
    queryKey: ['production-orders-history'],
    queryFn: () => base44.entities.ProductionOrder.list(),
  });

  const { data: prodTasks = [] } = useQuery({
    queryKey: ['production-tasks-history'],
    queryFn: () => base44.entities.ProductionTask.list(),
  });

  const { data: companySettingsList = [] } = useQuery({
    queryKey: ['company-settings'],
    queryFn: () => base44.entities.CompanySettings.list(),
  });
  const companySettings = companySettingsList?.[0] || {};

  const getDeletedBy = () => {
    if (clientUser) return clientUser.brand_name || 'Klien';
    return 'User';
  };

  const handleCancelPO = async (poId) => {
    if (confirm('Apakah Anda yakin ingin membatalkan dan menghapus pesanan ini?')) {
      await deletePO({ id: poId, deleted_by: getDeletedBy() });
    }
  };

  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    startSelection,
    selectedCount,
    totalCount
  } = useBatchSelection(clientPOs, po => po.id);

  const [isBatchDeleting, setIsBatchDeleting] = useState(false);
  const handleBatchDelete = async () => {
    if (confirm(`Apakah Anda yakin ingin membatalkan dan menghapus ${selectedCount} pesanan terpilih?`)) {
      setIsBatchDeleting(true);
      try {
        const deletedBy = getDeletedBy();
        for (const id of selectedIds) {
          await deletePO({ id, deleted_by: deletedBy });
        }
        clearSelection();
      } finally {
        setIsBatchDeleting(false);
      }
    }
  };

  const handlePaySimulate = async () => {
    if (!payModalPO) return;
    const isLunas = payDpPct === 100;
    const newStatus = isLunas ? 'LUNAS' : 'DP 50%';
    const newStatusProd = 'SEDANG PO ITEM';
    const totalJual = payModalPO.items.reduce((s, i) => s + i.hargaTotal, 0) + (payModalPO.shippingCost || 0);
    const nominal = isLunas ? totalJual : Math.round(totalJual * 0.5);
    
    const newRiwayat = [...(payModalPO.riwayatPembayaran || []), {
      jumlah: nominal,
      tanggal_bayar: format(new Date(), 'dd MMM yyyy'),
      catatan: `Pembayaran lanjutan via ${payMethod.toUpperCase()} (Terkonfirmasi Tim Satala)`
    }];
    const newTimeline = (payModalPO.timeline || []).map(t => {
      if (t.status === 'Pesanan Dibayar' || t.status === 'Dikonfirmasi Tim Satala') return { ...t, done: true, active: false, date: format(new Date(), 'dd MMM yyyy'), desc: `Pembayaran ${newStatus} Terkonfirmasi Tim Satala` };
      if (t.status === 'Menunggu Produksi' || t.status === 'Sedang PO Item') return { ...t, status: 'Sedang PO Item', done: true, active: true, date: format(new Date(), 'dd MMM yyyy'), desc: 'Pengadaan Bahan & Kemasan' };
      if (t.status === 'Pesanan Dibuat') return { ...t, active: false };
      return t;
    });
    
    await updatePO(payModalPO.id, {
      statusBayar: newStatus,
      statusProduksi: newStatusProd,
      dpPercentage: payDpPct,
      riwayatPembayaran: newRiwayat,
      timeline: newTimeline
    });
    
    setPayModalPO(null);
  };

  // Filter POs for current client
  const isDeveloper = 
    clientUser?.role === 'developer' || 
    clientUser?.role === 'admin' || 
    clientUser?.brand_name?.toLowerCase().includes('dev') || 
    clientUser?.email?.toLowerCase().includes('satalawebdeveloper') ||
    clientUser?.email?.toLowerCase().includes('dev');

  const assignedBrands = isDeveloper ? [] : (clientUser?.assigned_brands || (clientUser?.brand_name ? [clientUser.brand_name] : []));

  const filteredByClient = clientPOs.filter(po => {
    if (isDeveloper) return true;
    if (po.clientId === clientUser?.id) return true;
    if (po.customerEmail && clientUser?.email && po.customerEmail.toLowerCase() === clientUser.email.toLowerCase()) return true;
    if (assignedBrands.length > 0) {
      const poBrand = (po.brand_name || po.customerName || '').toLowerCase();
      return assignedBrands.some(b => poBrand.includes(b.toLowerCase()));
    }
    return po.clientId === 'cli_default' || !po.clientId;
  });

  const orders = (filteredByClient.length === 0 && !loadingPOs) ? defaultPOs : filteredByClient;

  // Helper hitung progres produksi real-time
  const getOrderProductionData = (ord) => {
    const matchedProdOrder = prodOrders.find(p => 
      p.order_number === ord.id || 
      p.id === ord.id || 
      (p.brand_name && ord.brand_name && p.brand_name.toLowerCase() === ord.brand_name.toLowerCase() && (p.order_number?.includes(ord.id) || ord.id?.includes(p.order_number)))
    );

    const orderTasks = prodTasks.filter(t => 
      t.po_id === matchedProdOrder?.id || 
      t.po_id === ord.id || 
      t.order_number === ord.id || 
      (matchedProdOrder?.order_number && t.order_number === matchedProdOrder.order_number)
    );

    let progressPercent = 0;
    let stageLabel = 'Belum Dikonfirmasi';
    let isCompleted = false;

    if (orderTasks.length > 0) {
      const completedCount = orderTasks.filter(t => t.status === 'completed').length;
      progressPercent = Math.round((completedCount / orderTasks.length) * 100);
      const inProgTask = orderTasks.find(t => t.status === 'in_progress');
      
      if (progressPercent === 100 || matchedProdOrder?.status === 'selesai' || matchedProdOrder?.status === 'siap_kirim') {
        progressPercent = 100;
        stageLabel = 'Selesai Produksi & QC';
        isCompleted = true;
      } else if (inProgTask) {
        stageLabel = `Sedang: ${inProgTask.task_name || inProgTask.activity_type || 'Proses Produksi'}`;
      } else {
        stageLabel = `${completedCount}/${orderTasks.length} Tahap Selesai`;
      }
    } else {
      const sp = (ord.statusProduksi || '').toUpperCase();
      const sb = (ord.statusBayar || '').toUpperCase();
      const prodStatus = (matchedProdOrder?.status || '').toLowerCase();

      if (sp.includes('SELESAI') || sp.includes('SIAP') || sp.includes('SAMPAI') || prodStatus === 'selesai' || prodStatus === 'siap_kirim') {
        progressPercent = 100;
        stageLabel = 'Selesai & Siap Kirim';
        isCompleted = true;
      } else if (sp.includes('KIRIM')) {
        progressPercent = 100;
        stageLabel = 'Pesanan Telah Dikirim';
        isCompleted = true;
      } else if (sp.includes('PROSES') || sp.includes('PROGRESS') || prodStatus === 'in_progress' || prodStatus === 'diracik' || prodStatus === 'maserasi' || prodStatus === 'filling' || prodStatus === 'packing') {
        progressPercent = 65;
        stageLabel = 'Sedang Diproduksi di Pabrik';
      } else if (
        prodStatus === 'antrian_produksi' || 
        sp === 'ANTREAN PRODUKSI' || 
        sp === 'ANTRIAN PRODUKSI' || 
        ord.items_ready === true || 
        ord.all_items_ready === true
      ) {
        progressPercent = 35;
        stageLabel = 'Antrean Produksi';
      } else if (
        prodStatus === 'sedang_po_item' || 
        sp === 'SEDANG PO ITEM' || 
        sp.includes('MENUNGGU PRODUKSI') || 
        (sb !== 'BELUM DIBAYAR' && sb !== 'BELUM DIKONFIRMASI' && !sb.includes('MENUNGGU'))
      ) {
        progressPercent = 25;
        stageLabel = 'Sedang PO Item';
      } else {
        progressPercent = 10;
        stageLabel = 'Menunggu Konfirmasi Tim Satala';
      }
    }

    const orderStatusUpper = (ord.statusProduksi || '').toUpperCase();
    const isCompletedFinal = isCompleted || 
      progressPercent === 100 || 
      orderStatusUpper.includes('SELESAI') || 
      orderStatusUpper.includes('SAMPAI') || 
      orderStatusUpper.includes('SIAP');

    return {
      matchedProdOrder,
      orderTasks,
      progressPercent,
      stageLabel,
      isCompleted: isCompletedFinal
    };
  };

  const getStatusBadge = (status) => {
    switch (status?.toUpperCase()) {
      case 'DRAFT':
      case 'BELUM NAIK PRODUKSI':
      case 'BELUM DIKONFIRMASI':
        return <span className="px-2 py-0.5 bg-rose-100 text-rose-700 text-[10px] font-black uppercase rounded-md border border-rose-200">BELUM DIKONFIRMASI</span>;
      case 'SEDANG PO ITEM':
      case 'SEDANG_PO_ITEM':
        return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-black uppercase rounded-md border border-amber-200">SEDANG PO ITEM</span>;
      case 'ANTREAN PRODUKSI':
      case 'ANTRIAN PRODUKSI':
      case 'MENUNGGU PRODUKSI':
        return <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] font-black uppercase rounded-md border border-indigo-200">ANTREAN PRODUKSI</span>;
      case 'PROSES PRODUKSI':
      case 'IN PROGRESS':
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-black uppercase rounded-md border border-blue-200">PROSES PRODUKSI</span>;
      case 'SELESAI':
        return <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-black uppercase rounded-md border border-green-200">SELESAI</span>;
      default:
        return <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-[10px] font-black uppercase rounded-md border border-slate-200">{status}</span>;
    }
  };

  const getPODateString = (ord) => {
    const rawDate = ord.date || ord.created_date || ord.created_at;
    if (!rawDate) return '';
    if (typeof rawDate === 'string' && rawDate.includes(' ')) {
      const months = { 'jan': '01', 'feb': '02', 'mar': '03', 'apr': '04', 'mei': '05', 'jun': '06', 'jul': '07', 'agu': '08', 'sep': '09', 'okt': '10', 'nov': '11', 'des': '12' };
      const parts = rawDate.trim().split(/\s+/);
      if (parts.length >= 3) {
        const day = parts[0].padStart(2, '0');
        const monthKey = parts[1].toLowerCase().substring(0, 3);
        const month = months[monthKey] || '01';
        const year = parts[2];
        return `${year}-${month}-${day}`;
      }
    }
    try {
      const d = new Date(rawDate);
      if (!isNaN(d.getTime())) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }
    } catch (e) {}
    return '';
  };

  // Logic filter & sort pesanan
  const filteredOrders = orders
    .filter(ord => {
      const labelProduk = ord.varianLabel || ord.items?.map(i => `${i.nama_product || i.nama}`).join(', ') || '';
      const matchesSearch = ord.id.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            labelProduk.toLowerCase().includes(searchQuery.toLowerCase());
      
      const sp = (ord.statusProduksi || '').toUpperCase();
      const sb = (ord.statusBayar || '').toUpperCase();
      const prodData = getOrderProductionData(ord);
      const stage = (prodData.stageLabel || '').toUpperCase();

      let matchesStatus = false;
      if (statusFilter === 'ALL') {
        matchesStatus = true;
      } else if (statusFilter === 'BELUM DIKONFIRMASI' || statusFilter === 'BELUM DIBAYAR') {
        matchesStatus = 
          sb.includes('BELUM') || 
          sb.includes('MENUNGGU') || 
          sb.includes('KONFIRMASI') || 
          sp.includes('BELUM') || 
          sp.includes('MENUNGGU VERIFIKASI') ||
          sp === 'DRAFT' ||
          stage.includes('KONFIRMASI') ||
          stage.includes('VERIFIKASI') ||
          stage.includes('BELUM');
      } else if (statusFilter === 'SEDANG PO ITEM') {
        matchesStatus = 
          sp.includes('PO ITEM') || 
          sp === 'SEDANG_PO_ITEM' || 
          stage.includes('PO ITEM');
      } else if (statusFilter === 'ANTREAN PRODUKSI' || statusFilter === 'ANTRIAN PRODUKSI') {
        matchesStatus = 
          sp.includes('ANTRIAN') || 
          sp.includes('ANTREAN') || 
          stage.includes('ANTREAN') || 
          stage.includes('ANTRIAN');
      } else if (statusFilter === 'PROSES PRODUKSI') {
        matchesStatus = 
          sp === 'PROSES PRODUKSI' || 
          sp === 'IN PROGRESS' || 
          sp.includes('PROSES') || 
          stage.includes('SEDANG:') || 
          stage.includes('PABRIK');
      } else if (statusFilter === 'SELESAI') {
        matchesStatus = 
          prodData.isCompleted || 
          sp.includes('SELESAI') || 
          sp.includes('SIAP') || 
          sp.includes('SAMPAI');
      } else {
        matchesStatus = sp === statusFilter || sb === statusFilter;
      }
      
      let matchesDate = true;
      if (dateFilter) {
        const ordDateStr = getPODateString(ord);
        matchesDate = ordDateStr === dateFilter;
      }
      
      return matchesSearch && matchesStatus && matchesDate;
    })
    .sort((a, b) => {
      const dateA = getPODateString(a);
      const dateB = getPODateString(b);
      if (sortOrder === 'latest') {
        return dateB.localeCompare(dateA);
      } else {
        return dateA.localeCompare(dateB);
      }
    });

  return (
    <div className="max-w-4xl mx-auto pb-20 relative">
      <BatchActionBar
        isSelectionMode={isSelectionMode}
        selectedCount={selectedCount}
        totalCount={filteredOrders.length}
        onSelectAll={selectAll}
        onClearSelection={clearSelection}
        onBatchDelete={handleBatchDelete}
        isDeleting={isBatchDeleting}
      />
      
      {/* CARD DAFTAR PESANAN (Persis Gambar Keempat) */}
      <div className="bg-white p-8 rounded-3xl border border-[#E6D5B8] shadow-sm mb-6">
        <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-[#302621] tracking-tight">Daftar Pesanan</h1>
            <p className="text-[#7A695B] text-xs font-medium mt-1">Lihat dan kelola semua pesanan Anda di sini</p>
          </div>
          <div className="shrink-0 flex gap-2">
            <Link to="/pesan-po" className="bg-[#8C6208] hover:bg-[#6F4E06] text-white px-5 py-2.5 rounded-xl font-bold text-xs shadow-sm transition-colors">
              + Buat PO Baru
            </Link>
          </div>
        </div>

        {/* SEARCH BAR (Tampilan Gambar Keempat) */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6 bg-[#F9F5EC] p-3 rounded-2xl border border-[#E6D5B8] print:hidden">
          {/* Sort Dropdown */}
          <div className="w-full sm:w-40 relative">
            <Select
              value={sortOrder}
              onValueChange={(val) => updateParams({ sort: val === 'latest' ? null : val })}
            >
              <SelectTrigger className="w-full h-[38px] text-xs font-black bg-white border border-[#E6D5B8] rounded-xl focus:ring-[#8C6208] text-[#302621]">
                <SelectValue placeholder="Urutkan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="latest" className="text-xs font-bold text-[#302621] cursor-pointer">Terbaru</SelectItem>
                <SelectItem value="oldest" className="text-xs font-bold text-[#302621] cursor-pointer">Terlama</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Search Input */}
          <div className="flex-1 relative">
            <input 
              type="text"
              value={searchQuery}
              onChange={(e) => updateParams({ q: e.target.value })}
              placeholder="Cari Pesanan"
              className="w-full h-[38px] pl-10 pr-4 text-xs bg-white border border-[#E6D5B8] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8C6208] text-[#302621] placeholder-slate-400 font-medium"
            />
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-2.5" />
          </div>

          {/* Calendar Picker */}
          <div className="w-full sm:w-48 relative flex items-center gap-1">
            <CustomDatePicker 
              value={dateFilter}
              onChange={(val) => updateParams({ date: val })}
              placeholder="Filter Tanggal"
              className="w-full h-[38px] text-xs font-bold bg-white border border-[#E6D5B8] rounded-xl focus:ring-2 focus:ring-[#8C6208] text-[#302621]"
            />
            {dateFilter && (
              <button 
                onClick={() => updateParams({ date: null })}
                className="text-xs text-slate-500 hover:text-red-600 font-bold px-2 shrink-0 transition-colors"
                title="Reset Filter Tanggal"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* TAB FILTERS (Kapsul Kategori) */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-100 pb-5 overflow-x-auto print:hidden">
          {[
            { label: 'Semua', value: 'ALL' },
            { label: 'Belum Dikonfirmasi', value: 'BELUM DIKONFIRMASI' },
            { label: 'Sedang PO Item', value: 'SEDANG PO ITEM' },
            { label: 'Antrean Produksi', value: 'ANTREAN PRODUKSI' },
            { label: 'Produksi', value: 'PROSES PRODUKSI' },
            { label: 'Selesai', value: 'SELESAI' }
          ].map((tab) => {
            const isActive = statusFilter === tab.value;
            return (
              <button
                key={tab.value}
                onClick={() => updateParams({ status: tab.value === 'ALL' ? null : tab.value })}
                className={`px-4 py-1.5 text-xs font-bold rounded-xl border transition-all ${
                  isActive 
                    ? 'bg-[#F9F5EC] text-[#8C6208] border-[#D4AF37] shadow-sm' 
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-[#F9F5EC]'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* LIST KARTU PESANAN (Bukan Tabel, Sangat Premium) */}
        {filteredOrders.length === 0 ? (
          <div className="py-16 text-center text-slate-400 font-medium bg-white rounded-2xl border border-slate-100 shadow-sm">
            Tidak ada pesanan yang sesuai dengan filter pencarian Anda.
          </div>
        ) : (
          <>
          <div className="space-y-4">
            {filteredOrders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((ord) => {
              const labelProduk = ord.varianLabel || (ord.items || []).map(i => `${i.nama_product || i.nama}`).join(', ');
              const totalBelanja = ord.grand_total || ord.total_amount || (ord.items || []).reduce((s, i) => s + (i.total_harga_jual || i.hargaTotal || 0), 0);
              const prodData = getOrderProductionData(ord);
              const firstItemName = ord.items?.[0]?.nama_product || ord.items?.[0]?.nama || ord.varianLabel || 'Varian Produk';
              const totalQty = (ord.items || []).reduce((s, i) => s + (i.qty || 1), 0);
              const displayDate = ord.date || (ord.created_date ? new Date(ord.created_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-');

              let statusColorClass = "bg-green-700 text-white border-green-800"; 
              let trackingStatusText = "Pesanan telah tiba";
              let displayBadgeText = ord.statusProduksi || 'PROSES';

              const spUpper = (ord.statusProduksi || '').toUpperCase();
              const sbUpper = (ord.statusBayar || '').toUpperCase();

              if (
                spUpper === 'BELUM NAIK PRODUKSI' || 
                spUpper === 'BELUM DIKONFIRMASI' ||
                spUpper === 'DRAFT' ||
                sbUpper === 'BELUM DIBAYAR' || 
                sbUpper === 'BELUM DIKONFIRMASI' ||
                sbUpper.includes('MENUNGGU') ||
                prodData.stageLabel === 'Menunggu Konfirmasi Tim Satala'
              ) {
                displayBadgeText = "BELUM DIKONFIRMASI";
                statusColorClass = "bg-rose-600 text-white border-rose-700";
                trackingStatusText = "Menunggu Konfirmasi Tim Satala";
              } else if (
                spUpper === 'SEDANG PO ITEM' || 
                spUpper === 'SEDANG_PO_ITEM' ||
                prodData.stageLabel === 'Sedang PO Item'
              ) {
                displayBadgeText = "SEDANG PO ITEM";
                statusColorClass = "bg-amber-600 text-white border-amber-700";
                trackingStatusText = "Bahan & Kemasan Sedang di-PO";
              } else if (
                spUpper === 'ANTREAN PRODUKSI' || 
                spUpper === 'ANTRIAN PRODUKSI' || 
                spUpper === 'MENUNGGU PRODUKSI' ||
                prodData.stageLabel === 'Antrean Produksi'
              ) {
                displayBadgeText = "ANTREAN PRODUKSI";
                statusColorClass = "bg-indigo-600 text-white border-indigo-700";
                trackingStatusText = "Item Ready - Masuk Antrean";
              } else if (
                spUpper === 'PROSES PRODUKSI' || 
                spUpper === 'IN PROGRESS' ||
                prodData.stageLabel.includes('Sedang:') ||
                prodData.stageLabel.includes('Pabrik')
              ) {
                displayBadgeText = "PROSES PRODUKSI";
                statusColorClass = "bg-blue-600 text-white border-blue-700";
                trackingStatusText = "Sedang diproduksi di pabrik";
              } else if (spUpper === 'SELESAI' || prodData.isCompleted) {
                displayBadgeText = "SELESAI";
                statusColorClass = "bg-emerald-600 text-white border-emerald-700";
                trackingStatusText = "Produksi Selesai & Siap Kirim";
              }

              return (
                <div 
                  key={ord.id} 
                  className={`bg-white border ${
                    isSelectionMode && selectedIds.has(ord.id) ? 'border-rose-500 ring-2 ring-rose-500 bg-rose-50/20' : 'border-[#E6D5B8]'
                  } rounded-2xl p-5 shadow-sm hover:shadow-md transition-all cursor-pointer`}
                  onClick={() => isSelectionMode && toggleSelection(ord.id)}
                >
                  
                  {/* Top Line */}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 border-b border-slate-100 pb-3 mb-4 text-xs">
                    <div className="flex flex-wrap items-center gap-2">
                      {isSelectionMode && (
                        <Checkbox 
                          checked={selectedIds.has(ord.id)} 
                          onCheckedChange={() => toggleSelection(ord.id)}
                          className="mr-1"
                        />
                      )}
                      <ShoppingBag className="w-4 h-4 text-slate-400" />
                      <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${statusColorClass}`}>
                        {displayBadgeText}
                      </span>
                      <span className="text-slate-300">|</span>
                      <span className="text-slate-600 font-medium">{displayDate}</span>
                      <span className="text-slate-300">|</span>
                      <span className="font-bold text-[#8C6208]">{ord.id}</span>
                      {ord.customerName && (
                        <>
                          <span className="text-slate-300">|</span>
                          <span className="text-slate-500 font-semibold">{ord.customerName}</span>
                        </>
                      )}
                      {ord.isSample && (
                        <span className="px-2 py-0.5 rounded text-[9px] font-black uppercase bg-purple-100 text-purple-700 tracking-wider">
                          SAMPLE
                        </span>
                      )}
                    </div>
                    <div className="text-slate-500 font-semibold flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${ord.statusProduksi === 'SELESAI' || prodData.isCompleted ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'}`}></span>
                      <span className="text-[10px] uppercase font-bold text-slate-400">{trackingStatusText}</span>
                    </div>
                  </div>

                  {/* Middle Area */}
                  <div className="flex flex-col sm:flex-row gap-5 items-start sm:items-center">
                    
                    {/* Left Thumbnail (PDF/File Mockup) */}
                    <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center shrink-0">
                      <div className="w-9 h-11 bg-red-50 border border-red-200 rounded flex flex-col items-center justify-center relative overflow-hidden">
                        <span className="text-[8px] font-black text-red-600 leading-none">PDF</span>
                        <div className="absolute bottom-0 inset-x-0 bg-red-600 h-1.5"></div>
                      </div>
                    </div>

                    {/* Center details */}
                    <div className="flex-1">
                      <h3 className="font-black text-[#302621] text-base leading-snug">
                        {firstItemName}
                        {(ord.items?.length || 0) > 1 && <span className="text-xs text-[#8C6208] font-bold ml-1"> (+{ord.items.length - 1} varian lainnya)</span>}
                      </h3>
                      <div className="text-xs text-[#7A695B] space-y-0.5 mt-1">
                        <p>Kuantitas: <span className="font-bold text-slate-800">{totalQty} pcs</span></p>
                        <p className="flex items-center flex-wrap gap-1">
                          <span>Termin:</span>
                          <span className="font-bold text-slate-800 uppercase">
                            {ord.paymentTerms === 'tempo' ? `Tempo ${ord.tempoDays || 30} Hari` : 'Cash (DP 50%)'}
                          </span>
                          <span className={`px-1.5 py-0.2 rounded text-[10px] font-bold ${
                            ord.statusBayar?.includes('TEMPO') 
                              ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                              : ord.statusBayar?.includes('DP') 
                                ? 'bg-amber-50 text-amber-700 border border-amber-200' 
                                : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          }`}>
                            {ord.statusBayar || 'LUNAS'}
                          </span>
                        </p>
                        {ord.paymentTerms === 'tempo' && ord.tanggal_jatuh_tempo && (
                          <p className="text-blue-700 font-bold text-[11px]">
                            Batas Jatuh Tempo: {formatTanggalIndo(ord.tanggal_jatuh_tempo)}
                          </p>
                        )}
                        <p>Kurir Pengiriman: <span className="font-semibold text-slate-700">{ord.courier || 'Kurir Ekspedisi'}</span></p>
                      </div>
                    </div>

                    {/* Right Total */}
                    <div className="text-left sm:text-right shrink-0">
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Total Belanja</p>
                      <p className="text-xl font-black text-[#302621] mt-0.5">Rp {fmt(totalBelanja + (ord.shippingCost || 0))}</p>
                    </div>

                  </div>

                  {/* Real-time Production Progress Bar */}
                  <div className="bg-[#FFFDF9] border border-[#E6D5B8]/80 rounded-2xl p-3.5 mt-4 mb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 text-xs mb-1.5">
                      <div className="flex items-center gap-1.5 font-bold text-[#302621]">
                        <Sparkles className="w-3.5 h-3.5 text-[#8C6208]" />
                        <span>Progres Produksi Real-Time:</span>
                        <span className="text-[#8C6208]">{prodData.stageLabel}</span>
                      </div>
                      <span className="font-black text-[#8C6208] text-xs">
                        {prodData.progressPercent}% Selesai
                      </span>
                    </div>

                    {/* Track */}
                    <div className="w-full bg-[#F9F5EC] border border-[#E6D5B8]/40 rounded-full h-2.5 overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 rounded-full ${
                          prodData.progressPercent === 100 
                            ? 'bg-gradient-to-r from-emerald-500 to-green-600' 
                            : 'bg-gradient-to-r from-[#D4AF37] to-[#8C6208]'
                        }`}
                        style={{ width: `${prodData.progressPercent}%` }}
                      />
                    </div>

                    {prodData.orderTasks.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-[#E6D5B8]/40 flex items-center justify-between text-[11px] text-[#7A695B]">
                        <span>
                          {prodData.orderTasks.filter(t => t.status === 'completed').length} dari {prodData.orderTasks.length} tahapan pabrik selesai
                        </span>
                        <button 
                          type="button" 
                          onClick={(e) => { e.stopPropagation(); setTaskDetailModalPO({ ...ord, prodData }); }}
                          className="text-[#8C6208] font-bold hover:underline flex items-center gap-1 cursor-pointer"
                        >
                          <Layers className="w-3 h-3" /> Lihat Tahapan Pabrik
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Bottom Right Actions */}
                  {!isSelectionMode && (
                    <div className="flex flex-wrap justify-end items-center gap-2.5 border-t border-slate-100 pt-3 mt-3 text-xs">
                      
                      {/* Tombol Download Surat Jalan (Aktif jika PO sudah selesai / siap kirim) */}
                      {prodData.isCompleted ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const sjPayload = {
                              id: ord.id,
                              order_number: ord.id,
                              brand_name: ord.brand_name || ord.customerName || clientUser?.brand_name || 'Mitra Satala',
                              customerName: ord.customerName || clientUser?.company_name || 'Klien Satala',
                              customerAddress: ord.customerAddress || clientUser?.address || 'Alamat Penerima',
                              phone: ord.phone || clientUser?.phone || '-',
                              courier: ord.courier || 'Kurir Ekspedisi',
                              resi: ord.resi || '-',
                              shipped_at: ord.date || ord.created_date,
                              products: (ord.products || ord.items || []).map(p => ({
                                product_name: p.nama_product || p.nama || 'Produk Maklon',
                                variant_name: p.nama_product || p.nama || 'Varian Produk',
                                botol_ukuran_label: p.selected_size || p.botol_ukuran_client || 30,
                                qty: p.qty || 1,
                                qty_akhir: p.qty || 1,
                                qty_dikirim: p.qty || 1
                              })),
                              items: (ord.items || []).map(p => ({
                                variant_name: p.nama_product || p.nama || 'Varian Produk',
                                qty_akhir: p.qty || 1,
                                qty_dikirim: p.qty || 1,
                                catatan: ''
                              })),
                              ...(prodData.matchedProdOrder || {})
                            };
                            setSuratJalanOrder(sjPayload);
                          }}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3.5 py-1.5 rounded-xl text-xs flex items-center gap-1.5 shadow-sm transition-colors cursor-pointer"
                        >
                          <Truck className="w-3.5 h-3.5" />
                          <span>Download Surat Jalan</span>
                        </button>
                      ) : (
                        <div 
                          className="text-[11px] font-semibold text-slate-400 flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 rounded-xl border border-slate-200 cursor-not-allowed"
                          title="Surat Jalan resmi dapat diunduh setelah tahapan produksi selesai"
                        >
                          <Truck className="w-3.5 h-3.5 text-slate-400" />
                          <span>Surat Jalan (Tersedia Saat Selesai)</span>
                        </div>
                      )}

                      {(ord.statusBayar === 'BELUM DIBAYAR' || ord.statusBayar === 'DP 50%') && (
                        <button onClick={(e) => { e.stopPropagation(); setPayModalPO(ord); }} className="bg-[#8C6208] hover:bg-[#6F4E06] text-white px-5 py-2 rounded-xl font-bold transition-colors shadow-sm">
                          Lanjutkan Pembayaran
                        </button>
                      )}
                      {ord.statusBayar === 'LUNAS' && (
                        <Link to="/pesan-po" onClick={e => e.stopPropagation()} className="bg-[#A1C038] hover:bg-[#8CA831] text-white px-5 py-2 rounded-xl font-bold transition-colors shadow-sm">
                          Beli Lagi
                        </Link>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); startSelection(ord.id); }} className="font-bold text-rose-500 hover:bg-rose-50 border border-rose-200 px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" /> Hapus
                      </button>
                      <Link to={`/pesanan/${ord.id}`} onClick={e => e.stopPropagation()} className="font-bold text-[#302621] hover:underline flex items-center gap-1 border border-slate-200 px-3 py-1.5 rounded-lg">
                        <FileText className="w-3.5 h-3.5"/> Detail
                      </Link>
                    </div>
                  )}

                </div>
              );
            })}
          </div>

          {/* ── Pagination Controls ── */}
          {filteredOrders.length > 0 && (
            <div className="px-6 py-4 border border-slate-100 rounded-2xl bg-white flex flex-wrap items-center justify-between gap-4 mt-6">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-bold text-slate-500">Row per Page</span>
                <div className="w-[80px]">
                  <select 
                    value={String(itemsPerPage)} 
                    onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    className="h-8 text-[11px] font-bold text-slate-700 bg-slate-50 border border-slate-200/80 rounded-xl px-2 w-full outline-none focus:ring-2 focus:ring-[#8C6208]/20"
                  >
                    {[5, 10, 25, 50, 100].map(n => (
                      <option key={n} value={String(n)} className="text-[11px] font-bold">{n}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex items-center gap-1.5">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &lt;
                </button>
                
                {(() => {
                  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
                  const pages = [];
                  for(let i=1; i<=totalPages; i++) {
                    if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-[11px] font-black transition-all ${
                            currentPage === i 
                              ? 'bg-[#8C6208] text-white shadow-md shadow-[#8C6208]/30' 
                              : 'text-slate-500 hover:bg-[#F9F5EC] hover:text-[#8C6208]'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    } else if (i === currentPage - 2 || i === currentPage + 2) {
                      pages.push(<span key={i} className="text-slate-400 text-[10px] px-1">...</span>);
                    }
                  }
                  return pages;
                })()}
                
                <button 
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredOrders.length / itemsPerPage), p + 1))}
                  disabled={currentPage >= Math.ceil(filteredOrders.length / itemsPerPage)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
          </>
        )}

      </div>

      {/* Floating Back to Top Button */}
      <button 
        onClick={scrollToTop}
        className="fixed bottom-[80px] md:bottom-8 right-6 md:right-8 z-50 w-12 h-12 bg-[#8C6208] hover:bg-[#6A4A06] text-white rounded-full shadow-xl shadow-[#8C6208]/30 flex items-center justify-center transition-transform hover:-translate-y-1 print:hidden border border-[#D4AF37]/30"
        title="Kembali ke Atas"
      >
        <ChevronUp className="w-6 h-6" />
      </button>

      {/* MODAL LANJUTKAN PEMBAYARAN */}
      {payModalPO && (
        <div className="fixed inset-0 bg-black/50  z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white">
              <h3 className="font-bold text-lg text-[#302621]">Lanjutkan Pembayaran</h3>
              <button onClick={() => setPayModalPO(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <p className="text-xs font-bold text-[#7A695B] mb-2 uppercase tracking-wider">Pilih Metode</p>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setPayMethod('bca')} className={`flex items-center gap-2 p-2.5 rounded-xl border-2 transition-all ${payMethod === 'bca' ? 'border-[#8C6208] bg-[#F9F5EC]' : 'border-slate-100 bg-white hover:border-[#E6D5B8]'}`}>
                    <Wallet className="w-4 h-4 text-slate-500" /> <span className="font-bold text-xs">Transfer BCA</span>
                  </button>
                  <button onClick={() => setPayMethod('qris')} className={`flex items-center gap-2 p-2.5 rounded-xl border-2 transition-all ${payMethod === 'qris' ? 'border-[#8C6208] bg-[#F9F5EC]' : 'border-slate-100 bg-white hover:border-[#E6D5B8]'}`}>
                    <QrCode className="w-4 h-4 text-slate-500" /> <span className="font-bold text-xs">QRIS</span>
                  </button>
                </div>
              </div>
              <div>
                <p className="text-xs font-bold text-[#7A695B] mb-2 uppercase tracking-wider">Skema Pembayaran</p>
                <div className="flex bg-slate-50 p-1 rounded-xl border border-slate-200">
                  <button onClick={() => setPayDpPct(50)} className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${payDpPct === 50 ? 'bg-white text-[#8C6208] shadow-sm border border-[#E6D5B8]' : 'text-slate-500 hover:text-[#8C6208]'}`}>DP 50%</button>
                  <button onClick={() => setPayDpPct(100)} className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${payDpPct === 100 ? 'bg-white text-[#8C6208] shadow-sm border border-[#E6D5B8]' : 'text-slate-500 hover:text-[#8C6208]'}`}>Lunas 100%</button>
                </div>
              </div>
            </div>
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-2">
              <button className="flex-1 border border-slate-200 bg-white py-2.5 rounded-xl font-bold text-xs hover:bg-slate-100" onClick={() => setPayModalPO(null)}>Batalkan</button>
              <button className="flex-1 bg-[#302621] text-white py-2.5 rounded-xl font-bold text-xs hover:bg-[#1A1411]" onClick={handlePaySimulate}>Konfirmasi & Bayar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SURAT JALAN PRINT / DOWNLOAD */}
      {suratJalanOrder && (
        <SuratJalanPrint
          order={suratJalanOrder}
          companySettings={companySettings}
          onClose={() => setSuratJalanOrder(null)}
        />
      )}

      {/* MODAL CHECKLIST TAHAPAN PRODUKSI */}
      {taskDetailModalPO && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[85vh] border border-[#E6D5B8]">
            <div className="px-6 py-4 border-b border-[#E6D5B8] bg-[#F9F5EC] flex justify-between items-center">
              <div>
                <span className="text-[10px] font-bold text-[#8C6208] uppercase tracking-wider block">Tahapan Produksi Pabrik</span>
                <h3 className="font-black text-base text-[#302621]">{taskDetailModalPO.id}</h3>
              </div>
              <button 
                onClick={() => setTaskDetailModalPO(null)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-3 text-xs">
              <div className="bg-[#FFFDF9] p-4 rounded-2xl border border-[#E6D5B8] mb-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-[#302621]">Status Keseluruhan:</span>
                  <span className="font-black text-[#8C6208]">{taskDetailModalPO.prodData?.progressPercent}%</span>
                </div>
                <p className="text-[#7A695B] text-[11px]">{taskDetailModalPO.prodData?.stageLabel}</p>
              </div>

              <p className="font-bold text-[#302621] uppercase text-[10px] tracking-wider mb-2">Checklist Aktivitas:</p>
              
              <div className="divide-y divide-gray-100 border border-[#E6D5B8]/60 rounded-2xl overflow-hidden bg-white">
                {(taskDetailModalPO.prodData?.orderTasks || []).map((t, idx) => {
                  const isDone = t.status === 'completed';
                  const isInProg = t.status === 'in_progress';

                  return (
                    <div key={idx} className="p-3.5 flex items-center justify-between hover:bg-[#FFFDF9]">
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs ${
                          isDone 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : isInProg 
                            ? 'bg-blue-100 text-blue-700 animate-pulse' 
                            : 'bg-slate-100 text-slate-400'
                        }`}>
                          {isDone ? '✓' : idx + 1}
                        </div>
                        <div>
                          <p className="font-bold text-[#302621]">{t.task_name || t.activity_type}</p>
                          <p className="text-[10px] text-[#7A695B] capitalize">
                            {t.product_name || 'Global Aktivitas'} • {t.activity_type || 'Produksi'}
                          </p>
                        </div>
                      </div>

                      <div>
                        {isDone ? (
                          <span className="bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-emerald-200">
                            Selesai
                          </span>
                        ) : isInProg ? (
                          <span className="bg-blue-50 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-blue-200">
                            Sedang Dikerjakan
                          </span>
                        ) : (
                          <span className="bg-slate-50 text-slate-500 text-[10px] font-medium px-2 py-0.5 rounded-full">
                            Dalam Antrean
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="px-6 py-4 bg-[#F9F5EC] border-t border-[#E6D5B8] text-right">
              <button 
                onClick={() => setTaskDetailModalPO(null)}
                className="text-xs font-bold px-5 py-2 rounded-xl border border-[#E6D5B8] bg-white hover:bg-slate-50 text-[#302621]"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
